import { useState } from 'react'

import { api } from '../../api/client'
import { t } from '../../i18n/lingua'
import { SceltaCIParametro } from './SceltaCIParametro'
import { etichettaProposta, type SerieScelta } from './SceltaSerie'

/** A link as saved in the widget's options: two interface ends, each with a name of its own. */
export interface LinkScelto {
  a: SerieScelta | null
  b: SerieScelta | null
  /** The names of the two ends («Sede Milano», «DC Roma»): empty = the CI's name. */
  label_a: string
  label_b: string
}

export const LINK_VUOTO: LinkScelto = { a: null, b: null, label_a: '', label_b: '' }

/**
 * Whether two port names are the same port: «Gi1/0/3» and «GigabitEthernet1/0/3»
 * are, «Gi1/0/3» and «Gi1/0/13» are not. Case and spaces do not count; when
 * the spelling differs, the numeric tail must match and the first two
 * letters too (Gi/GigabitEthernet, Te/TenGigabitEthernet, Et/Ethernet).
 */
export function stessaPorta(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  const na = a.toLowerCase().replace(/\s+/g, '')
  const nb = b.toLowerCase().replace(/\s+/g, '')
  if (na === nb) return true
  const coda = (s: string) => /(\d+(?:[/:.]\d+)*)$/.exec(s)?.[1] ?? ''
  return coda(na) !== '' && coda(na) === coda(nb) && na.slice(0, 2) === nb.slice(0, 2)
}

/**
 * The two ends of a link. The far end is **proposed from the adjacency**
 * (LLDP/CDP: the relation `connected_to` of the near CI whose local port is
 * the chosen interface), then looked up among the far CI's monitored
 * interfaces by port name — and it stays editable: the adjacency is a hint,
 * the port description is what the person reads. Without a monitored far
 * end the link is drawn one-sided (a WAN towards the provider).
 */
export function SceltaLink({ link, onCambia }: { link: LinkScelto; onCambia: (link: LinkScelto) => void }) {
  const [cerco, setCerco] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)

  async function proponi() {
    if (!link.a?.ci_id || !link.a.key) return
    setCerco(true)
    setEsito(null)
    try {
      const portaA = link.a.label.split(' · ')[1] ?? ''
      const relazioni = await api.ciRelations(link.a.ci_id)
      const vicine = relazioni.filter((r) => r.peer_id && r.attributes && (r.via === 'lldp' || r.via === 'cdp' || r.rel_type === 'connected_to'))
      const mia = (r: typeof vicine[number]) => String(r.direction === 'outgoing' ? r.attributes.local_port ?? '' : r.attributes.remote_port ?? '')
      const sua = (r: typeof vicine[number]) => String(r.direction === 'outgoing' ? r.attributes.remote_port ?? '' : r.attributes.local_port ?? '')
      const giusta = vicine.find((r) => stessaPorta(mia(r), portaA) || stessaPorta(mia(r), link.a?.key.replace(/^if:(name:)?/, '')))
        ?? (vicine.length === 1 ? vicine[0] : undefined)
      if (!giusta || !giusta.peer_id) {
        setEsito(t('Nessuna adiacenza LLDP/CDP su questa porta: scegli l’altro capo a mano.'))
        return
      }
      const lontano = await api.ciEagle(giusta.peer_id)
      const porte = (lontano.params ?? []).filter((p) => p.kind === 'interface')
      const portaB = sua(giusta)
      const p = porte.find((q) => stessaPorta(q.name, portaB) || stessaPorta(String(q.extra?.if_name ?? ''), portaB))
      if (!p) {
        setEsito(t('Adiacenza trovata: {ci} porta {porta}, ma quella porta non è monitorata da Eagle. Attivala sul CI o scegli un’altra interfaccia.', { ci: giusta.peer_name ?? '', porta: portaB || '?' }))
        return
      }
      const nomeCI = giusta.peer_name ?? ''
      onCambia({
        ...link,
        b: { ci_id: giusta.peer_id, key: p.key, ci_name: nomeCI, unit: p.unit, kind: p.kind,
             label: etichettaProposta(nomeCI, { key: p.key, name: p.name, kind: p.kind, detail: p.detail ?? '', unit: p.unit ?? '' }) },
      })
      setEsito(t('Dall’adiacenza {via}: {ci} · {porta}{stato}', {
        via: (giusta.via ?? 'lldp').toUpperCase(), ci: nomeCI, porta: p.name,
        stato: p.enabled ? '' : ` (${t('parametro spento')})`,
      }))
    } catch {
      setEsito(t('Adiacenze non leggibili al momento.'))
    } finally {
      setCerco(false)
    }
  }

  const capo = (quale: 'a' | 'b') => {
    const s = link[quale]
    return (
      <fieldset className="serie-scelta__riga">
        <legend>{quale === 'a' ? t('Capo A (il più vicino)') : t('Capo B (l’altro capo)')}</legend>
        <div className="form-grid">
          <SceltaCIParametro ciId={s?.ci_id ?? ''} chiave={s?.key ?? ''} soloTipi={['interface']}
                             onCambia={(ci_id, key, ci_name, p) => onCambia({
                               ...link,
                               [quale]: ci_id ? { ci_id, key, ci_name, unit: p?.unit, kind: p?.kind, label: p ? etichettaProposta(ci_name, p) : (s?.label ?? '') } : null,
                             })} />
          <label className="field span-3">
            <span className="field__label">{t('Nome del capo')}</span>
            <input type="text" value={quale === 'a' ? link.label_a : link.label_b} maxLength={60}
                   placeholder={s?.ci_name || (quale === 'a' ? t('es. Sede Milano') : t('es. DC Roma'))}
                   onChange={(e) => onCambia({ ...link, [quale === 'a' ? 'label_a' : 'label_b']: e.target.value })} />
          </label>
          {quale === 'a' && (
            <div className="field span-3 serie-scelta__azioni">
              <button type="button" className="secondary" disabled={!s?.ci_id || !s.key || cerco} onClick={() => void proponi()}>
                {cerco ? t('Cerco…') : t('Proponi l’altro capo dall’adiacenza')}
              </button>
            </div>
          )}
          {quale === 'b' && s && (
            <div className="field span-3 serie-scelta__azioni">
              <button type="button" className="linklike" onClick={() => onCambia({ ...link, b: null })}>{t('nessun altro capo (link a un solo capo)')}</button>
            </div>
          )}
        </div>
      </fieldset>
    )
  }

  return (
    <div className="serie-scelta">
      {capo('a')}
      {esito && <p className="muted eagle-piccolo" style={{ margin: 0 }}>{esito}</p>}
      {capo('b')}
    </div>
  )
}
